import React, { useEffect, useState } from "react";

/**
 * CONFIG
 */
const COOKIE_CONSENT_KEY = "tc_cookie_consent_v2";
const COOKIE_CONSENT_NAME = "tc_cookie_consent";
const CONSENT_EXPIRY_DAYS = 180; // ~6 mois

const defaultConsent = {
    necessary: true, // toujours actif
    preferences: false,
    analytics: false,
    marketing: false,
    updatedAt: null,
    expiresAt: null,
    version: 2,
};

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function isExpired(expiresAt) {
    if (!expiresAt) return true;
    const expiry = new Date(expiresAt).getTime();
    if (Number.isNaN(expiry)) return true;
    return Date.now() > expiry;
}

/**
 * COOKIE HELPERS
 */
function setCookie(name, value, days) {
    try {
        const expires = addDays(new Date(), days).toUTCString();
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
            value
        )}; expires=${expires}; path=/; SameSite=Lax`;
    } catch (e) {
        console.error("Erreur setCookie:", e);
    }
}

function getCookie(name) {
    try {
        const target = `${encodeURIComponent(name)}=`;
        const cookies = document.cookie ? document.cookie.split("; ") : [];
        for (const c of cookies) {
            if (c.startsWith(target)) {
                return decodeURIComponent(c.slice(target.length));
            }
        }
        return null;
    } catch (e) {
        console.error("Erreur getCookie:", e);
        return null;
    }
}

/**
 * STORAGE HELPERS
 */
function readStoredConsent() {
    try {
        const local = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (local) return JSON.parse(local);
    } catch (e) {
        console.error("Erreur lecture localStorage cookies:", e);
    }

    try {
        const cookieRaw = getCookie(COOKIE_CONSENT_NAME);
        if (cookieRaw) return JSON.parse(cookieRaw);
    } catch (e) {
        console.error("Erreur lecture cookie cookies:", e);
    }

    return null;
}

function persistStoredConsent(payload) {
    try {
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(payload));
    } catch (e) {
        console.error("Erreur sauvegarde localStorage cookies:", e);
    }

    try {
        setCookie(COOKIE_CONSENT_NAME, JSON.stringify(payload), CONSENT_EXPIRY_DAYS);
    } catch (e) {
        console.error("Erreur sauvegarde cookie cookies:", e);
    }
}

/**
 * SCRIPT HELPERS (GA4 / Matomo)
 * - Tu peux en garder un seul si tu veux.
 */

/* ========= GA4 ========= */
function ensureGtagStub() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.gtag =
        window.gtag ||
        function gtag() {
            window.dataLayer.push(arguments);
        };
}

// Charge gtag.js (une seule fois)
function loadGA4(measurementId) {
    if (!measurementId || typeof document === "undefined") return;
    if (document.querySelector(`script[data-ga4="${measurementId}"]`)) return;

    ensureGtagStub();

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    s.dataset.ga4 = measurementId;
    document.head.appendChild(s);

    window.gtag("js", new Date());
    // Important: consent mode par défaut "denied" tant que pas accepté
    window.gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        functionality_storage: "granted", // optionnel
        security_storage: "granted",
    });
}

// Met à jour le consentement GA4
function updateGA4Consent(consent) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;

    window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
        functionality_storage: consent.preferences ? "granted" : "denied",
        security_storage: "granted",
    });
}

/* ========= Matomo ========= */
// Matomo peut être préchargé, puis activé/désactivé par consentement
function ensureMatomoQueue() {
    if (typeof window === "undefined") return;
    window._paq = window._paq || [];
}

function applyMatomoConsent(consent) {
    if (typeof window === "undefined") return;
    ensureMatomoQueue();

    // Si tu utilises Matomo consent API
    // Nécessite config Matomo correspondante côté script
    if (consent.analytics) {
        window._paq.push(["rememberConsentGiven"]);
        window._paq.push(["setConsentGiven"]);
    } else {
        window._paq.push(["forgetConsentGiven"]);
        window._paq.push(["requireConsent"]);
    }
}

/**
 * MAIN COMPONENT
 *
 * Props:
 * - onConsentChange(consent)
 * - ga4MeasurementId?: string
 * - useMatomo?: boolean
 */
const CookieConsent = ({
                           onConsentChange,
                           ga4MeasurementId,
                           useMatomo = false,
                       }) => {
    const [isBannerOpen, setIsBannerOpen] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [consent, setConsent] = useState(defaultConsent);
    const [isReady, setIsReady] = useState(false);


    // Initialise stubs / mode deny par défaut AVANT consentement (important)
    useEffect(() => {
        if (ga4MeasurementId) {
            loadGA4(ga4MeasurementId);
        }
        if (useMatomo) {
            ensureMatomoQueue();
            // On force requireConsent au départ
            window._paq.push(["requireConsent"]);
        }
    }, [ga4MeasurementId, useMatomo]);

    // Lecture consentement + expiration
    useEffect(() => {
        const stored = readStoredConsent();

        if (!stored || isExpired(stored.expiresAt)) {
            setConsent(defaultConsent);
            setIsBannerOpen(true);
            setIsReady(true);
            return;
        }

        const merged = {
            ...defaultConsent,
            ...stored,
            necessary: true,
        };

        setConsent(merged);
        setIsBannerOpen(false);
        setIsReady(true);

        // Appliquer aux scripts
        if (ga4MeasurementId) updateGA4Consent(merged);
        if (useMatomo) applyMatomoConsent(merged);

        if (typeof onConsentChange === "function") {
            onConsentChange(merged);
        }
    }, [ga4MeasurementId, useMatomo, onConsentChange]);

    const persistConsent = (nextConsent) => {
        const now = new Date();
        const expiresAt = addDays(now, CONSENT_EXPIRY_DAYS).toISOString();

        const payload = {
            ...defaultConsent,
            ...nextConsent,
            necessary: true,
            updatedAt: now.toISOString(),
            expiresAt,
            version: 2,
        };

        setConsent(payload);
        persistStoredConsent(payload);

        // Appliquer immédiatement aux outils
        if (ga4MeasurementId) updateGA4Consent(payload);
        if (useMatomo) applyMatomoConsent(payload);

        setIsBannerOpen(false);
        setIsPanelOpen(false);

        if (typeof onConsentChange === "function") {
            onConsentChange(payload);
        }
    };

    const handleAcceptAll = () => {
        persistConsent({
            necessary: true,
            preferences: true,
            analytics: true,
            marketing: true,
        });
    };

    const handleRejectAll = () => {
        persistConsent({
            necessary: true,
            preferences: false,
            analytics: false,
            marketing: false,
        });
    };

    const handleSaveChoices = () => {
        persistConsent(consent);
    };

    const toggleCategory = (key) => {
        if (key === "necessary") return;
        setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // API publique: ouvrir panneau depuis footer
    useEffect(() => {
        window.openCookieSettings = () => {
            setIsPanelOpen(true);
            setIsBannerOpen(false);
        };
        return () => {
            delete window.openCookieSettings;
        };
    }, []);

    // Optionnel: API publique pour lire le consentement
    useEffect(() => {
        window.getCookieConsent = () => ({ ...consent });

        return () => {
            delete window.getCookieConsent;
        };
    }, [consent]);

// Bloque le scroll tant qu'un choix n'a pas été fait (bannière)
// + pendant l'ouverture du panneau de préférences
    useEffect(() => {
        if (typeof document === "undefined") return;

        const shouldLockScroll = isBannerOpen || isPanelOpen;

        const previousOverflow = document.body.style.overflow;
        const previousTouchAction = document.body.style.touchAction;

        if (shouldLockScroll) {
            document.body.style.overflow = "hidden";
            document.body.style.touchAction = "none"; // utile mobile
        } else {
            document.body.style.overflow = previousOverflow || "";
            document.body.style.touchAction = previousTouchAction || "";
        }

        return () => {
            document.body.style.overflow = previousOverflow || "";
            document.body.style.touchAction = previousTouchAction || "";
        };
    }, [isBannerOpen, isPanelOpen]);

    if (!isReady) return null;



    return (
        <>
            {isBannerOpen && (
                <div className="tc-cookie" role="dialog" aria-live="polite" aria-label="Gestion des cookies">
                    <div className="tc-cookie__card">
                        <div className="tc-cookie__content">
                            <h3 className="tc-cookie__title">Cookies</h3>
                            <p className="tc-cookie__text">
                                Nous utilisons des cookies essentiels pour faire fonctionner le site,
                                et avec votre accord des cookies de mesure d’audience, de préférences
                                et de marketing. Vous pouvez accepter, refuser ou personnaliser.
                            </p>
                        </div>

                        <div className="tc-cookie__actions">
                            <button className="tc-cookie__btn tc-cookie__btn--ghost" type="button" onClick={handleRejectAll}>
                                Tout refuser
                            </button>
                            <button className="tc-cookie__btn tc-cookie__btn--ghost" type="button" onClick={() => setIsPanelOpen(true)}>
                                Personnaliser
                            </button>
                            <button className="tc-cookie__btn tc-cookie__btn--primary" type="button" onClick={handleAcceptAll}>
                                Tout accepter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isPanelOpen && (
                <div className="tc-cookie-modal" role="dialog" aria-modal="true" aria-labelledby="tc-cookie-modal-title">
                    <div
                        className="tc-cookie-modal__backdrop"
                        onClick={() => setIsPanelOpen(false)}
                        aria-hidden="true"
                    />

                    <div className="tc-cookie-modal__panel">
                        <div className="tc-cookie-modal__header">
                            <h3 id="tc-cookie-modal-title" className="tc-cookie-modal__title">
                                Préférences cookies
                            </h3>
                            <button
                                type="button"
                                className="tc-cookie-modal__close"
                                onClick={() => setIsPanelOpen(false)}
                                aria-label="Fermer"
                            >
                                ✕
                            </button>
                        </div>

                        <p className="tc-cookie-modal__desc">
                            Les cookies essentiels sont toujours actifs. Vous pouvez autoriser
                            ou refuser les autres catégories à tout moment.
                        </p>

                        <div className="tc-cookie-modal__list">
                            <CookieRow
                                title="Essentiels"
                                description="Nécessaires au fonctionnement du site (sécurité, navigation, session)."
                                checked={true}
                                disabled
                                onChange={() => {}}
                            />
                            <CookieRow
                                title="Préférences"
                                description="Mémorise vos préférences d’affichage, langue, ou options d’interface."
                                checked={consent.preferences}
                                onChange={() => toggleCategory("preferences")}
                            />
                            <CookieRow
                                title="Analytics"
                                description="Mesure l’audience et les performances du site (ex: pages vues, navigation)."
                                checked={consent.analytics}
                                onChange={() => toggleCategory("analytics")}
                            />
                            <CookieRow
                                title="Marketing"
                                description="Cookies publicitaires / campagnes / personnalisation (si activés sur votre site)."
                                checked={consent.marketing}
                                onChange={() => toggleCategory("marketing")}
                            />
                        </div>

                        <div className="tc-cookie-modal__footer">
                            <button className="tc-cookie__btn tc-cookie__btn--ghost" type="button" onClick={handleRejectAll}>
                                Tout refuser
                            </button>
                            <button className="tc-cookie__btn tc-cookie__btn--ghost" type="button" onClick={handleAcceptAll}>
                                Tout accepter
                            </button>
                            <button className="tc-cookie__btn tc-cookie__btn--primary" type="button" onClick={handleSaveChoices}>
                                Enregistrer mes choix
                            </button>
                        </div>

                        {consent.updatedAt && (
                            <p className="tc-cookie-modal__meta">
                                Consentement mis à jour le{" "}
                                {new Date(consent.updatedAt).toLocaleString("fr-FR")} •
                                Expire le{" "}
                                {consent.expiresAt
                                    ? new Date(consent.expiresAt).toLocaleDateString("fr-FR")
                                    : "—"}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

const CookieRow = ({ title, description, checked, onChange, disabled = false }) => {
    return (
        <div className={`tc-cookie-row ${disabled ? "is-disabled" : ""}`}>
            <div className="tc-cookie-row__text">
                <div className="tc-cookie-row__title">{title}</div>
                <p className="tc-cookie-row__desc">{description}</p>
            </div>

            <label className="tc-cookie-switch" aria-label={title}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                />
                <span className="tc-cookie-switch__track" />
            </label>
        </div>
    );
};

export default CookieConsent;
export { COOKIE_CONSENT_KEY, COOKIE_CONSENT_NAME };
